import type {Icon} from '@primer/octicons-react'
import {
  AlertIcon,
  ArchiveIcon,
  BeakerIcon,
  BookmarkIcon,
  BriefcaseIcon,
  BugIcon,
  CalendarIcon,
  ClockIcon,
  CodeReviewIcon,
  CodescanIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  DependabotIcon,
  EyeIcon,
  FileDiffIcon,
  FlameIcon,
  GearIcon,
  GitPullRequestIcon,
  GlobeIcon,
  HomeIcon,
  HubotIcon,
  IssueOpenedIcon,
  LinkIcon,
  MentionIcon,
  MeterIcon,
  MoonIcon,
  NorthStarIcon,
  OrganizationIcon,
  PeopleIcon,
  PersonIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  RepoTemplateIcon,
  RocketIcon,
  SmileyIcon,
  SquirrelIcon,
  SunIcon,
  TelescopeIcon,
  TerminalIcon,
  ToolsIcon,
  ZapIcon,
} from '@primer/octicons-react'

export interface UserShortcut {
  id: string
  name: string
  searchType: 'repositories' | 'issues' | 'pull-requests'
  query: string
  description?: string
  icon: IconName
  color: IconColor
}

export interface SidebarLink extends UserShortcut {
  source: 'predefined' | 'user'
}

/// Returns the class name for the icon color based on the provided color string.
export function getIconColorClassName(color: string) {
  const key = color.toLowerCase() as IconColor
  return colorMap[key].foreground
}

export function getBackgroundIconColorClassName(color: string) {
  const key = color.toLowerCase() as IconColor
  return colorMap[key].background
}

export function getIconComponent(name: string): Icon {
  const key = name.toLowerCase() as IconName
  return iconMap[key] || BookmarkIcon
}

export function getDisplayName(name: string) {
  return `${name.charAt(0).toUpperCase() + name.slice(1).replaceAll('_', ' ')}`
}

const colorMap = {
  ['gray']: {foreground: 'color-fg-muted', background: 'color-bg-emphasis'},
  ['blue']: {foreground: 'color-fg-accent', background: 'color-bg-accent-emphasis'},
  ['green']: {foreground: 'color-fg-success', background: 'color-bg-success-emphasis'},
  ['orange']: {foreground: 'color-fg-severe', background: 'color-bg-severe-emphasis'},
  ['red']: {foreground: 'color-fg-danger', background: 'color-bg-danger-emphasis'},
  ['pink']: {foreground: 'color-fg-sponsors', background: 'color-bg-sponsors-emphasis'},
  ['purple']: {foreground: 'color-fg-done', background: 'color-bg-done-emphasis'},
} as const

export type IconColor = keyof typeof colorMap

export const supportedIconColors = Object.keys(colorMap) as IconColor[]

const iconMap = {
  ['people']: PeopleIcon,
  ['smiley']: SmileyIcon,
  ['mention']: MentionIcon,
  ['clock']: ClockIcon,
  ['git_pull_request']: GitPullRequestIcon,
  ['zap']: ZapIcon,
  ['issue_opened']: IssueOpenedIcon,
  ['person']: PersonIcon,
  ['comment_discussion']: CommentDiscussionIcon,
  ['organization']: OrganizationIcon,
  ['briefcase']: BriefcaseIcon,
  ['file_diff']: FileDiffIcon,
  ['code_review']: CodeReviewIcon,
  ['codescan']: CodescanIcon,
  ['terminal']: TerminalIcon,
  ['tools']: ToolsIcon,
  ['beaker']: BeakerIcon,
  ['alert']: AlertIcon,
  ['eye']: EyeIcon,
  ['telescope']: TelescopeIcon,
  ['bookmark']: BookmarkIcon,
  ['calendar']: CalendarIcon,
  ['meter']: MeterIcon,
  ['moon']: MoonIcon,
  ['sun']: SunIcon,
  ['flame']: FlameIcon,
  ['bug']: BugIcon,
  ['north_star']: NorthStarIcon,
  ['rocket']: RocketIcon,
  ['squirrel']: SquirrelIcon,
  ['hubot']: HubotIcon,
  ['dependabot']: DependabotIcon,
  ['repo']: RepoIcon,
  ['repo_locked']: RepoLockedIcon,
  ['link']: LinkIcon,
  ['repo_forked']: RepoForkedIcon,
  ['repo_template']: RepoTemplateIcon,
  ['archive']: ArchiveIcon,
  ['home']: HomeIcon,
  ['gear']: GearIcon,
  ['copilot']: CopilotIcon,
  ['globe']: GlobeIcon,
} as const

export type IconName = keyof typeof iconMap

export const supportedIconNames = Object.keys(iconMap) as IconName[]
